import React from 'react';
import { useChildDailyActivities } from '../hooks/useDashboard';
import { ActivityIcon } from '../../../shared/components';

export const DailyActivities = ({ child, date, onClose, t, formatNumber, formatDate }) => {
  const { data, isLoading, error } = useChildDailyActivities(child?.id, date, !!child && !!date);

  if (!child || !date) return null;

  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '0m';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0) return `${remainingSeconds}s`;
    if (remainingSeconds === 0) return `${minutes}m`;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-link p-0 me-3 text-dark"
                onClick={onClose}
                aria-label={t('common.back')}
              >
                <i className="bi bi-arrow-left" style={{ fontSize: '20px' }}></i>
              </button>
              <div>
                <h5 className="modal-title mb-0">
                  {t('dashboard.index.children.daily_activities.title', { date: formatDate(date, 'long') })}
                </h5>
                <small className="text-muted">{child.username}</small>
              </div>
            </div>
          </div>
          <div className="modal-body">
            {isLoading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t('common.loading')}</span>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger">
                {t('common.error')}: {error.message}
              </div>
            )}

            {data && (
              <>
                {/* Summary Stats */}
                <div className="row g-3 mb-4">
                  <div className="col-6">
                    <div className="bg-light rounded-3 p-3 text-center">
                      <div className="fw-bold text-primary" style={{ fontSize: '24px' }}>
                        {formatNumber(data.total_xp)}
                      </div>
                      <small className="text-muted">{t('dashboard.index.children.daily_activities.total_xp')}</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="bg-light rounded-3 p-3 text-center">
                      <div className="fw-bold text-info" style={{ fontSize: '24px' }}>
                        {formatDuration(data.total_duration_seconds)}
                      </div>
                      <small className="text-muted">{t('dashboard.index.children.daily_activities.total_duration')}</small>
                    </div>
                  </div>
                </div>

                {/* Activities List */}
                {data.activities.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-calendar-x" style={{ fontSize: '48px' }}></i>
                    <p className="mt-3">{t('dashboard.index.children.daily_activities.no_activities')}</p>
                  </div>
                ) : (
                  <div className="list-group">
                    {data.activities.map((activity) => (
                      <div key={activity.id} className="list-group-item">
                        <div className="d-flex align-items-start">
                          <ActivityIcon
                            iconUrl={activity.activity_icon}
                            name={activity.activity_name}
                            size={48}
                            showBackground={true}
                            className="me-3"
                          />
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start">
                              <h6 className="mb-1">{activity.activity_name}</h6>
                              {activity.earned_xp != null && (
                                <span className="badge bg-success">+{formatNumber(activity.earned_xp)} PowerKid XP</span>
                              )}
                            </div>
                            {activity.notes && (
                              <div className="mb-2">
                                <span className="badge bg-info text-dark">
                                  <i className="bi bi-trophy me-1"></i>
                                  {activity.notes}
                                </span>
                              </div>
                            )}
                            <div className="d-flex flex-wrap gap-3 text-muted small">
                              <span>
                                <i className="bi bi-clock me-1"></i>
                                {formatTime(activity.started_at)} - {formatTime(activity.completed_at)}
                              </span>
                              <span>
                                <i className="bi bi-hourglass-split me-1"></i>
                                {formatDuration(activity.duration_seconds)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
